<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';
	import { Alert, Button } from '@Shelly/shelly-design-system';

	import { selectDevicesByRoom } from '../../../../GenericComponents/helpers';
	import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';

	import type { Device } from 'src/types/Device';

	import {
		navigateToShop,
		generateWhenAndDoMissingDevices,
		sortByPosition,
	} from '../../../../../../../../components/Lists/list-helpers';
	import {
		excludedCategoriesFromDoActions,
		excludedDeviceTypesFromDoActions,
		includedDeviceTypesForDoActions,
	} from 'src/components/Scenes/helpers/init_device_actions';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import { is_device_action_ui } from 'src/types/DeviceActionUI';
	import { _init_action_components_v2 } from '../../../Device/helpers';
	import { DO_WIZARD_STEPS_INDEXES } from '../../helpers/do-wizard-helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		accessibleVirtualDevice,
		accessibleVirtualDevices,
		isFirstVirtualDevice,
	} from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { defaultHiddenDevicesRoom } from 'src/helpers/constants';
	import { isTestUser } from 'src/helpers/user_helpers';
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';
	import EmptyList from 'src/components/Lists/EmptyList.svelte';
	import { real_id, isCctOnlyDevice } from 'src/helpers/interface_helpers';
	export let sceneData: any;
	export let firstSelectedRef: HTMLElement = null;

	const exclude_categories = excludedCategoriesFromDoActions;
	let exclude_types = excludedDeviceTypesFromDoActions;
	let exclude_shared = true;
	let missingDevice: {
		title: string;
		value: string | undefined;
		id: string;
	} = null;

	if (is_device_action_ui(sceneData.translated)) {
		let _id =
			Number(sceneData.translated.device.channel) > 0
				? `${sceneData.translated.device.id}_${sceneData.translated.device.channel}`
				: sceneData.translated.device.id;
		sceneData.selectedDevice = {
			value: _id,
			title: $devices[_id]?.name || 'Missing or deleted device',
			id: _id,
		};

		sceneData.deviceAction.actions = [...sceneData.translated.actions];

		let _device = $devices[sceneData.selectedDevice.value];

		if (_device) {
			sceneData.deviceAction.components = _init_action_components_v2(
				$devices[_device.id],
				readDeviceStatus(real_id(_device.id)),
				readDeviceSettings(real_id(_device.id)),
			);
		} else {
			missingDevice = {
				title: _id,
				value: _id || undefined,
				id: _id,
			};
		}

		if (sceneData.translated.delay !== undefined) {
			sceneData.deviceAction.delay = { ...sceneData.translated.delay };
		}
		if (sceneData.translated.timer !== undefined) {
			sceneData.deviceAction.timer = { ...sceneData.translated.timer };
		}
	}

	// Multiple Virtual devices (more than 1) is a premium feature
	// If basic user has more than 1 virtual device, the rest of them will be disabled
	const prepareVirtualDeviceInList = (device: Device) => {
		const disabledVirtualDevice = !accessibleVirtualDevice(device);
		return {
			...device,
			disabled: disabledVirtualDevice,
			badge: !isFirstVirtualDevice(device)
				? $format('label_premium', { default: 'Premium' })
				: undefined,
		};
	};

	const mapRoomDevicesSelectedProp = (room, deviceSelected) => {
		const updatedCards = room.cards.map((device) => {
			let device_item = device;
			if (device.category === 'virtual_device') {
				device_item = prepareVirtualDeviceInList(device);
			}
			return {
				...device_item,
				selected: device.id === deviceSelected?.id,
			};
		});

		return {
			...room,
			cards: updatedCards,
		};
	};

	const handle_device_and_find_action_options = () => {
		let _device = $devices[sceneData.selectedDevice.value];
		if (_device) {
			sceneData.deviceAction.components = _init_action_components_v2(
				$devices[_device.id],
				readDeviceStatus(real_id(_device.id)),
				readDeviceSettings(real_id(_device.id)),
			);

			// sceneData.deviceAction.actions = [];//it is not necessary to clear actions because if user select another device and then return to the existing one the actions will be cleared and it looks glitchy
		} else {
			sceneData.deviceAction.components = [];
		}
		sceneData.deviceAction.delay = {
			units: 'sec',
			value: 0,
		};

		sceneData.deviceAction.timer = {
			units: 'sec',
			value: 0,
		};
	};

	const validateWizardStepSelectDevice = () => {
		sceneData.isValid = false;
		sceneData.validStepIndex = DO_WIZARD_STEPS_INDEXES.CHANGE_STATE;
	};

	const syncDeviceStepValidation = () => {
		const shouldInvalidate =
			(sceneData.do_info.mode === 'edit' && Object.keys(sceneData.selectedDevice).length === 0) ||
			(sceneData.do_info.mode === 'add' && !sceneData.selectedDevice.selected) ||
			!$devices[sceneData.selectedDevice?.id];

		if (shouldInvalidate) {
			sceneData.isValid = false;
			sceneData.validStepIndex = DO_WIZARD_STEPS_INDEXES.DEVICE_STEP;
		}
	};

	const onSelectDevice = (device) => {
		validateWizardStepSelectDevice();

		sceneData.selectedDevice = {
			...device,
			selected: true,
		};

		const selectedDevice = $devices[device.id];
		if (selectedDevice?.category === 'virtual_device' && !accessibleVirtualDevice(selectedDevice)) {
			sceneData.isValid = false;
			sceneData.validStepIndex = DO_WIZARD_STEPS_INDEXES.DEVICE_STEP;
		}

		sortedRoomsWithDevices = sortedRoomsWithDevices.map((room) => {
			return mapRoomDevicesSelectedProp(room, device);
		});
		handle_device_and_find_action_options();
	};

	onMount(syncDeviceStepValidation);

	$: baseSortedRoomsWithDevices = Object.values({
		...$rooms,
		...(isTestUser() && {
			[defaultHiddenDevicesRoom.value as number]: {
				name: $format(defaultHiddenDevicesRoom.title, {
					default: defaultHiddenDevicesRoom.translationDefault,
				}),
				id: defaultHiddenDevicesRoom.value,
			},
		}),
	})
		.sort(sortByPosition)
		.map((room: any) => {
			return { title: room.name, value: room.id };
		})
		.map((room) => {
			return {
				cards: selectDevicesByRoom(
					$devices,
					room,
					exclude_categories,
					exclude_types,
					exclude_shared,
					true,
					includedDeviceTypesForDoActions,
				)
					.filter((device) => {
						if (device.category === 'module') {
							const _device = $devices[device.id];
							/**
							 * CCT-only devices (like S3MX) should always be included as they support actions
							 */
							if (isCctOnlyDevice(_device?.type)) {
								return true;
							}
							/**
							 * module in 'inputs_reader' and 'shell' mode doesn't have control components
							 */
							const deviceMode = $devices[device.id].mode;
							return !/^(inputs_reader|shell)$/.test(deviceMode);
						}
						return true;
					})
					.sort(sortByPosition),
				roomID: room.value,
				roomName: room.title,
			};
		})
		.filter((room) => {
			return room.cards.length > 0;
		})
		.map((room) => {
			return mapRoomDevicesSelectedProp(room, sceneData.selectedDevice);
		});

	$: sortedRoomsWithDevices = missingDevice?.title
		? [{ ...generateWhenAndDoMissingDevices(missingDevice) }, ...baseSortedRoomsWithDevices]
		: baseSortedRoomsWithDevices;
</script>

{#if !accessibleVirtualDevices()}
	<Alert
		class="mb-m mt-m"
		type="info"
		customIcon="fas fa-gem icon-premium-color"
		heading={$format('alert_title_unlock_virtual_devices_scenes', {
			default: 'Multiple Virtual Devices is a Premium feature. Upgrade to Premium to unlock.',
		})}
	>
		<Button
			slot="bottom"
			variant="premium"
			size="m"
			width="auto"
			on:click={() => {
				resetDrawerClose();
				push('/settings/premium');
			}}
		>
			{capFirstChar(
				$format('button_get_premium', {
					default: 'Get Premium',
				}),
			)}
		</Button>
	</Alert>
{/if}
<SelectList
	missingDevicesAlertHeading={$format('missing_devices_alert_label', {
		values: { type: $format('process_scene_action', { default: 'action' }) },
		default: 'You have missing device(s) in this {type}.',
	})}
	class="mb-s"
	title={$format('label_select_device', {
		default: 'Select device',
	})}
	list={sortedRoomsWithDevices}
	variant="radio"
	selectedCardsId={[sceneData.selectedDevice]}
	on:click={(e) => onSelectDevice(e.detail)}
	bind:firstSelectedRef
/>

{#if !sortedRoomsWithDevices.length}
	<div class="d-flex flex-direction-column justify-content-center align-items-center mt-s">
		<EmptyList
			withoutMargin
			type="modal"
			title={$format('on_board_get_device_title', {
				default: 'Get the necessary devices to set up.',
			})}
			description={$format('on_board_get_device_description', {
				default:
					'Currently you don’t have the necessary devices. Please get the necessary devices to set up your automations.',
			})}
			btnText={capFirstChar(
				$format('button_go_to_store', {
					default: 'Go to store',
				}).toLocaleLowerCase(),
			)}
			onClick={() => {
				navigateToShop();
			}}
		/>
	</div>
{/if}
